import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Plus, CheckCircle, XCircle, Lock, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { resolveBackendUrl } from '../lib/utils';

const BACKEND_URL = resolveBackendUrl();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const BlockchainPage = () => {
  const [blockchain, setBlockchain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    asteroid_id: '',
    asteroid_name: '',
    is_profitable: true,
    predicted_roi: '',
    mining_approved: true,
    operator: 'AstroMining Corp',
  });

  useEffect(() => {
    fetchBlockchain();
  }, []);

  const fetchBlockchain = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blockchain`);
      setBlockchain(response.data);
    } catch (error) {
      console.error('Fetch blockchain error:', error);
      toast.error('Failed to load blockchain');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/blockchain/add`, {
        asteroid_id: formData.asteroid_id,
        asteroid_name: formData.asteroid_name,
        is_profitable: formData.is_profitable,
        predicted_roi: parseFloat(formData.predicted_roi),
        mining_approved: formData.mining_approved,
        operator: formData.operator,
      });

      toast.success('Mining operation added to blockchain!');
      setDialogOpen(false);
      fetchBlockchain();
      
      // Reset form
      setFormData({
        asteroid_id: '',
        asteroid_name: '',
        is_profitable: true,
        predicted_roi: '',
        mining_approved: true,
        operator: 'AstroMining Corp',
      });
    } catch (error) {
      console.error('Add block error:', error);
      toast.error('Failed to add block: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const getBlockColor = (index) => {
    const colors = [
      'from-cyan-500/20 to-blue-500/20 border-cyan-500/30',
      'from-purple-500/20 to-pink-500/20 border-purple-500/30',
      'from-yellow-500/20 to-orange-500/20 border-yellow-500/30',
      'from-green-500/20 to-teal-500/20 border-green-500/30',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-600 flex items-center justify-center">
              <Database className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold gradient-text mb-3" data-testid="page-title">Mining Blockchain Ledger</h1>
          <p className="text-gray-400 mb-6" data-testid="page-description">Immutable record of all mining operations</p>

          <div className="flex justify-center gap-4 mb-6">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="cosmic-button" data-testid="add-transaction-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Mining Operation
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-card border-white/10 text-white" data-testid="add-transaction-dialog">
                <DialogHeader>
                  <DialogTitle>New Mining Operation</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Record a new asteroid mining transaction
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddBlock} className="space-y-4">
                  <div>
                    <Label htmlFor="ast-id" className="text-gray-300">Asteroid ID</Label>
                    <Input
                      id="ast-id"
                      data-testid="input-asteroid-id"
                      value={formData.asteroid_id}
                      onChange={(e) => setFormData({ ...formData, asteroid_id: e.target.value })}
                      placeholder="AST-001"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="ast-name-tx" className="text-gray-300">Asteroid Name</Label>
                    <Input
                      id="ast-name-tx"
                      data-testid="input-asteroid-name-tx"
                      value={formData.asteroid_name}
                      onChange={(e) => setFormData({ ...formData, asteroid_name: e.target.value })}
                      placeholder="16 Psyche"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="roi-tx" className="text-gray-300">Predicted ROI (%)</Label>
                    <Input
                      id="roi-tx"
                      data-testid="input-roi-tx"
                      type="number"
                      step="0.01"
                      value={formData.predicted_roi}
                      onChange={(e) => setFormData({ ...formData, predicted_roi: e.target.value })}
                      placeholder="350.5"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <Label htmlFor="profitable-switch" className="text-gray-300 cursor-pointer">Is Profitable</Label>
                    <Switch
                      id="profitable-switch"
                      data-testid="switch-profitable"
                      checked={formData.is_profitable}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_profitable: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <Label htmlFor="approved-switch" className="text-gray-300 cursor-pointer">Mining Approved</Label>
                    <Switch
                      id="approved-switch"
                      data-testid="switch-approved"
                      checked={formData.mining_approved}
                      onCheckedChange={(checked) => setFormData({ ...formData, mining_approved: checked })}
                    />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full cosmic-button" data-testid="submit-transaction-button">
                    {loading ? 'Adding...' : 'Add to Blockchain'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Button
              variant="outline"
              onClick={fetchBlockchain}
              disabled={loading}
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {blockchain && (
            <div className="flex justify-center gap-6 text-sm" data-testid="blockchain-stats">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-green-400" />
                <span className="text-gray-400">Chain Status:</span>
                <span className={blockchain.is_valid ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
                  {blockchain.is_valid ? 'Valid' : 'Invalid'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-cyan-400" />
                <span className="text-gray-400">Total Blocks:</span>
                <span className="text-cyan-400 font-semibold">{blockchain.length}</span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Blockchain Blocks */}
        <div className="space-y-6">
          {blockchain && blockchain.chain ? (
            <>
              {blockchain.chain.slice().reverse().map((block, idx) => (
                <motion.div
                  key={block.index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  data-testid={`block-${block.index}`}
                >
                  <Card className={`glass-card border bg-gradient-to-br ${getBlockColor(block.index)}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                            #{block.index}
                          </div>
                          {block.data.type === 'genesis' ? 'Genesis Block' : block.data.asteroid_name || 'Mining Operation'}
                        </CardTitle>
                        {block.data.mining_approved !== undefined && (
                          <div data-testid={`block-${block.index}-status`}>
                            {block.data.mining_approved ? (
                              <div className="flex items-center gap-1 text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-xs">Approved</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-red-400">
                                <XCircle className="w-4 h-4" />
                                <span className="text-xs">Rejected</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <CardDescription className="text-gray-400 text-xs">
                        {formatTimestamp(block.timestamp)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Block Data */}
                      {block.data.type === 'genesis' ? (
                        <div className="p-3 rounded-lg bg-white/5" data-testid={`block-${block.index}-genesis-data`}>
                          <p className="text-sm text-gray-300">{block.data.message}</p>
                          <p className="text-xs text-gray-500 mt-1">Version: {block.data.version}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {block.data.asteroid_id && (
                            <div className="p-3 rounded-lg bg-white/5" data-testid={`block-${block.index}-asteroid-id`}>
                              <div className="text-xs text-gray-400 mb-1">Asteroid ID</div>
                              <div className="text-sm font-semibold text-white">{block.data.asteroid_id}</div>
                            </div>
                          )}
                          {block.data.predicted_roi !== undefined && (
                            <div className="p-3 rounded-lg bg-white/5" data-testid={`block-${block.index}-roi`}>
                              <div className="text-xs text-gray-400 mb-1">Predicted ROI</div>
                              <div className="text-sm font-semibold text-cyan-400">{block.data.predicted_roi}%</div>
                            </div>
                          )}
                          {block.data.is_profitable !== undefined && (
                            <div className="p-3 rounded-lg bg-white/5" data-testid={`block-${block.index}-profitable`}>
                              <div className="text-xs text-gray-400 mb-1">Profitable</div>
                              <div className={`text-sm font-semibold ${block.data.is_profitable ? 'text-green-400' : 'text-red-400'}`}>
                                {block.data.is_profitable ? 'Yes' : 'No'}
                              </div>
                            </div>
                          )}
                          {block.data.operator && (
                            <div className="p-3 rounded-lg bg-white/5" data-testid={`block-${block.index}-operator`}>
                              <div className="text-xs text-gray-400 mb-1">Operator</div>
                              <div className="text-sm font-semibold text-purple-400">{block.data.operator}</div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Hashes */}
                      <div className="space-y-2">
                        <div className="p-2 rounded bg-black/30" data-testid={`block-${block.index}-hash`}>
                          <div className="text-xs text-gray-500 mb-1">Block Hash</div>
                          <code className="text-xs text-green-400 font-mono break-all">{block.hash}</code>
                        </div>
                        <div className="p-2 rounded bg-black/30" data-testid={`block-${block.index}-prev-hash`}>
                          <div className="text-xs text-gray-500 mb-1">Previous Hash</div>
                          <code className="text-xs text-gray-500 font-mono break-all">{block.previous_hash}</code>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </>
          ) : (
            <Card className="glass-card border-white/10" data-testid="blockchain-loading">
              <CardContent className="flex flex-col items-center justify-center py-16">
                {loading ? (
                  <>
                    <div className="loading-spinner mb-4"></div>
                    <p className="text-gray-500">Loading blockchain...</p>
                  </>
                ) : (
                  <>
                    <Database className="w-16 h-16 text-gray-600 mb-4" />
                    <p className="text-gray-500">No blockchain data available</p>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlockchainPage;
